import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import type { ThemeMode } from "@buoy-x/domain";

export const nativeTokens = {
  light: {
    page: "#f2f7fa",
    surface: "#ffffff",
    elevated: "#e8f2f7",
    panelMuted: "#edf4f7",
    text: "#08243a",
    muted: "#5f7484",
    border: "#c9dbe5",
    ocean: "#00253d",
    oceanRaised: "#073a57",
    oceanBorder: "#21556f",
    oceanText: "#f6fbff",
    oceanMuted: "#a9c2d1",
    primary: "#ff4f0a",
    blue: "#147bd1",
    success: "#087d50",
    warning: "#a65b00",
    destructive: "#c92f3e",
  },
  dark: {
    page: "#001427",
    surface: "#0b263d",
    elevated: "#12344e",
    panelMuted: "#0f2d45",
    text: "#f4f8fb",
    muted: "#a9bbc9",
    border: "#35536b",
    ocean: "#00111f",
    oceanRaised: "#082b42",
    oceanBorder: "#234f68",
    oceanText: "#f6fbff",
    oceanMuted: "#9eb8c8",
    primary: "#ff5a17",
    blue: "#54a5ff",
    success: "#50e889",
    warning: "#ffc342",
    destructive: "#ff6974",
  },
} as const;

interface ThemeContextValue {
  mode: ThemeMode;
  resolved: "light" | "dark";
  setMode: (mode: ThemeMode) => void;
  syncMode: (mode: ThemeMode) => void;
}
const ThemeContext = createContext<ThemeContextValue | null>(null);

const systemDark = () =>
  typeof matchMedia !== "undefined" &&
  matchMedia("(prefers-color-scheme: dark)").matches;

export function ThemeProvider({
  children,
  onChange,
}: PropsWithChildren<{ onChange?: (mode: ThemeMode) => void }>) {
  const [mode, setModeState] = useState<ThemeMode>(
    () =>
      (localStorage.getItem("buoy-x-theme") as ThemeMode | null) ?? "system",
  );
  const [dark, setDark] = useState(systemDark);
  const resolved = mode === "system" ? (dark ? "dark" : "light") : mode;

  useEffect(() => {
    const media = matchMedia("(prefers-color-scheme: dark)");
    const update = () => setDark(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
  }, [resolved]);
  const syncMode = useCallback((next: ThemeMode) => {
    localStorage.setItem("buoy-x-theme", next);
    setModeState(next);
  }, []);
  const setMode = useCallback(
    (next: ThemeMode) => {
      syncMode(next);
      onChange?.(next);
    },
    [onChange, syncMode],
  );
  const value = useMemo(
    () => ({ mode, resolved, setMode, syncMode }),
    [mode, resolved, setMode, syncMode],
  );
  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme must be used inside ThemeProvider");
  return value;
}
